import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { PaginatedResponse } from "@/shared/http";

import { AgentThreadRecord } from "../AgentThreadRecord";

interface SetThreadPinnedParams {
  threadId: string;
  pinned: boolean;
}

type SidebarPage = PaginatedResponse<AgentThreadRecord>;

interface SetThreadPinnedContext {
  /** Every sidebar page as it stood before the optimistic patch. */
  previousPages: [QueryKey, SidebarPage | undefined][];
}

/**
 * Key prefix of the sidebar's thread list, matched against every org / page
 * variant the cache holds.
 */
const threadsForUserQueryKey = ["agentThreadsForUser"];

/**
 * React Query hook for pinning or unpinning a thread for the signed-in user.
 *
 * A pin is personal, so anyone who can read a thread can pin it — including a
 * teammate's org-shared thread. That is wider than `useUpdateThreadVisibility`,
 * which is creator-only; callers do not need to gate the affordance on
 * ownership.
 *
 * The optimistic patch below flips `pinned_at` in place rather than moving the
 * row; the sidebar reorders on the stamped value, so the newly pinned thread
 * renders in the same slot the refetch will land it in. That timestamp has to
 * be a real one for the same reason — a placeholder would put the row in the
 * wrong slot until the refetch corrected it, which is the jump this avoids.
 * The server still owns the order: it caps the list at 100 rows and sorts
 * pinned rows first, and the invalidation below brings back the real thing.
 */
export function useSetThreadPinned() {
  const { agentThreadService } = useDomainServices();
  const orgId = useCurrentOrgId();
  const queryClient = useQueryClient();
  const showNotification = useNotification();

  const patchCachedPinnedAt = (threadId: string, pinned: boolean) => {
    queryClient.setQueriesData<SidebarPage>(
      { queryKey: threadsForUserQueryKey },
      (page) =>
        page === undefined
          ? page
          : {
              ...page,
              items: page.items.map((record) =>
                record.thread_id === threadId
                  ? {
                      ...record,
                      // "Now" both flips the control and sorts the row to the
                      // top of the pinned block, where the server's own
                      // pin-time ordering will also put it; the refetch this
                      // mutation triggers then replaces it with the server's
                      // timestamp.
                      pinned_at: pinned ? new Date().toISOString() : null,
                    }
                  : record,
              ),
            },
    );
  };

  return useMutation<
    void,
    Error,
    SetThreadPinnedParams,
    SetThreadPinnedContext
  >({
    mutationFn: async ({ threadId, pinned }) => {
      return agentThreadService.setThreadPinned(threadId, pinned, { orgId });
    },
    onMutate: async ({ threadId, pinned }) => {
      // A list refetch already in flight resolves from a snapshot taken
      // before the write and would land on top of the patch below, putting
      // the old pin state back. Awaiting the cancel lets those requests abort
      // and their query state settle first.
      await queryClient.cancelQueries({ queryKey: threadsForUserQueryKey });

      // Snapshot rather than recompute: an inverse patch invents a fresh
      // `pinned_at` (the sidebar sorts the pinned block by it) and cannot
      // restore the absence of the field, and two clicks on one thread
      // overlapping in flight make "the opposite of what I asked for" the
      // wrong answer entirely.
      const previousPages = queryClient.getQueriesData<SidebarPage>({
        queryKey: threadsForUserQueryKey,
      });
      patchCachedPinnedAt(threadId, pinned);
      return { previousPages };
    },
    onSuccess: () => {
      // Key prefixes, so every org / page / filter variant refetches: a pin
      // changes the order rows come back in, and pinning an org-shared thread
      // pulls a teammate's thread into a list that did not hold it before.
      void queryClient.invalidateQueries({
        queryKey: threadsForUserQueryKey,
      });
      void queryClient.invalidateQueries({ queryKey: ["agentThreadSearch"] });
    },
    onError: (_error, { pinned }, context) => {
      // Put the snapshot back, then let the server have the last word.
      for (const [queryKey, page] of context?.previousPages ?? []) {
        queryClient.setQueryData(queryKey, page);
      }
      void queryClient.invalidateQueries({
        queryKey: threadsForUserQueryKey,
      });
      // A failed write leaves the control showing the old state, which is
      // indistinguishable from a click that never registered.
      showNotification(
        pinned ? "Couldn't pin this thread" : "Couldn't unpin this thread",
        "error",
      );
    },
  });
}
